/**
 * LOS TEXTOS DE PEDIDOS ONLINE, A DEMANDA (hallazgo COST-03).
 *
 * Antes translations.js importaba TODO pedidos (config, rules, pedir, editor y pos: 345 KB, 62 KB en
 * brotli) y todo eso viajaba en el main.js que baja cualquiera: la landing de las campañas de Meta,
 * el panel, el editor y comandas de los ~220 que no tienen pedidos online. Ahora el main lleva solo
 * `pos` (la bandeja y los avisos de comandas, que corren también en la POS del local) y las otras
 * áreas viajan en el pedazo de quien las usa:
 *
 *   config  pedidos.cfg.*     la configuración de Comandas (OnlineOrdersSection) y «Pedidos por
 *                             WhatsApp» del editor (WhatsappOrdersForm / onlineWhatsappService)
 *   rules   pedidos.ed.*      «Revisá tu carta», «Cómo se pide», «Opciones para elegir», «Probá un
 *                             pedido» (src/v2/components/ordering, utils/ordering/describe)
 *   pedir   pedidos.pedir.*   los rótulos que el editor toma de /pedir (describe, TryOrder, HowToOrder)
 *   editor  pedidos.editor.*  integridad del editor y «Pedidos por WhatsApp» (MenuContextV2,
 *                             CatalogSection, WhatsappOrdersCard)
 *
 * CÓMO: cada módulo que usa un área importa su cargador (`./load/<área>`), que la suma al diccionario
 * cuando se evalúa ese módulo, es decir ANTES de que pinte nada. Los textos de pedidos online que
 * muestran pantallas que baja todo el mundo (el modal de categoría y el de plato, con la puerta
 * abierta) esperan a su área con usePedidosArea. Y si a algún módulo nuevo le faltara su cargador, t()
 * baja el área sola la primera vez que no encuentra una clave (el texto aparece al instante
 * siguiente) en vez de quedar mostrando la clave para siempre.
 *
 * En los TESTS el diccionario trae todo, como antes (translations.js): los tests de claves y de
 * pantallas lo leen directo. Lo de producción lo cubre i18nLazy.test.js.
 */
import translations from "../translations";

const LANGS = ["es", "en", "pt"];
const loaded = new Set();
const listeners = new Set();

/** Suma un área al diccionario (una vez) y avisa (I18nProvider vuelve a pintar). */
export function registerPedidosArea(name, area) {
  if (loaded.has(name) || !area) return;
  loaded.add(name);
  for (const lang of LANGS) {
    const src = area[lang] || {};
    const dst = translations[lang];
    if (!dst) continue;
    // Solo lo que falta: nunca pisa una clave que ya estaba (todas empiezan con "pedidos.").
    for (const k of Object.keys(src)) if (!(k in dst)) dst[k] = src[k];
  }
  listeners.forEach((fn) => {
    try {
      fn(name);
    } catch (_) {
      /* un oyente roto no frena a los demás */
    }
  });
}

export function pedidosAreaLoaded(name) {
  return loaded.has(name) || (process.env.NODE_ENV === "test" && name in IMPORTERS);
}

export function onPedidosArea(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* Los pedazos, por área (webpack arma uno por cada import()). */
const IMPORTERS = {
  config: () => import("./config"),
  rules: () => import("./rules"),
  pedir: () => import("./pedir"),
  editor: () => import("./editor"),
};

const inflight = new Map();
/** Baja un área si hace falta. Nunca rechaza: true si quedó cargada. */
export function loadPedidosArea(name) {
  if (pedidosAreaLoaded(name)) return Promise.resolve(true);
  if (!IMPORTERS[name]) return Promise.resolve(false);
  if (inflight.has(name)) return inflight.get(name);
  const p = IMPORTERS[name]()
    .then((m) => {
      registerPedidosArea(name, (m && m.default) || m);
      return true;
    })
    .catch(() => false) // red mala o un deploy en el medio: la próxima vez se reintenta
    .finally(() => inflight.delete(name));
  inflight.set(name, p);
  return p;
}

/** De qué área es una clave ("pedidos.cfg.title" → "config"). null si no es de un área a demanda. */
export function areaOfKey(key) {
  const m = /^pedidos\.(cfg|ed|pedir|editor)\./.exec(String(key || ""));
  if (!m) return null;
  return { cfg: "config", ed: "rules", pedir: "pedir", editor: "editor" }[m[1]];
}
